import React, { useEffect, useState } from "react";
import { useI18n } from "@/i18n";
import { useSettings } from "@/context/SettingsContext";
import api from "@/lib/api";
import MeritCard from "@/components/MeritCard";
import { PaperSection, SectionTitle, CloudDivider } from "@/components/common";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Flower2, GraduationCap, ScrollText, PlayCircle, CalendarHeart } from "lucide-react";

// Convert a pasted share URL (TikTok / YouTube) into an embeddable iframe src.
function toVideoEmbed(url) {
  const u = (url || "").trim();
  if (!u) return { src: "", vertical: false };
  if (u.includes("tiktok.com")) {
    const m = u.match(/(?:\/video\/|\/v\/|embed\/v2\/)(\d{6,})/);
    if (m) return { src: `https://www.tiktok.com/embed/v2/${m[1]}`, vertical: true };
    return { src: u, vertical: true };
  }
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return { src: `https://www.youtube.com/embed/${yt[1]}`, vertical: false };
  return { src: u, vertical: false };
}

export default function Lotus() {
  const { t, lang } = useI18n();
  const { settings } = useSettings();
  const [items, setItems] = useState([]);
  const lotus = settings?.content?.lotus || {};
  const meaning = lotus[`meaning_${lang}`] || lotus.meaning_zh || "";
  const videos = lotus.videos || [];

  useEffect(() => {
    api.get("/projects", { params: { category: "lotus" } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);

  const lightItems = items.filter((p) => ["large", "small108", "paper", ""].includes(p.lotus_type) && p.lotus_type !== "class" && p.lotus_type !== "blessing");
  const classItems = items.filter((p) => p.lotus_type === "class");
  const blessingItems = items.filter((p) => p.lotus_type === "blessing");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
      <div className="text-center mb-6">
        <Flower2 className="h-9 w-9 mx-auto text-[hsl(var(--brand-bronze))]" />
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("nav.lotus")}</h1>
        <p className="mt-2 text-muted-foreground">{lang === "zh" ? "折莲修心 · 点亮莲花 · 为至亲祈福" : "Fold lotus · Light a lotus · Bless your loved ones"}</p>
      </div>
      <CloudDivider />

      {/* Meaning */}
      <PaperSection className="mt-6">
        <SectionTitle zh="折莲花的意义" en="Meaning of Folding Lotus" lang={lang} className="mb-3" />
        <p className="text-[15px] leading-7 text-foreground/85 whitespace-pre-line">{meaning}</p>
      </PaperSection>

      {/* Light a lotus */}
      <div className="mt-10">
        <SectionTitle zh="点亮莲花功德" en="Light a Lotus" lang={lang} kicker="OFFERING" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {lightItems.map((p) => <MeritCard key={p.id} project={p} />)}
        </div>
      </div>

      {/* 1st-day blessing */}
      {blessingItems.length > 0 && (
        <div className="mt-10">
          <SectionTitle zh="初一莲花祈福" en="1st-Day Lotus Blessing" lang={lang} kicker="BLESSING" />
          <div className="flex items-center gap-2 mb-4 text-sm text-[hsl(var(--brand-bronze))]">
            <CalendarHeart className="h-4 w-4" />
            {lang === "zh" ? "报名后请完整个人资料，报名记录可在个人页面查看。" : "Complete your profile after signing up; records appear on your profile."}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {blessingItems.map((p) => <MeritCard key={p.id} project={p} />)}
          </div>
        </div>
      )}

      {/* Class */}
      {classItems.length > 0 && (
        <div className="mt-10">
          <SectionTitle zh="莲花折叠班 · 报名" en="Lotus Folding Class" lang={lang} kicker="CLASS" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {classItems.map((p) => <MeritCard key={p.id} project={p} />)}
          </div>
        </div>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <div className="mt-10">
          <SectionTitle zh="莲花教学视频" en="Teaching Videos" lang={lang} kicker="LEARN" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {videos.map((v, i) => {
              const { src, vertical } = toVideoEmbed(v.url);
              return (
                <div key={i} className="rounded-2xl overflow-hidden border border-border bg-card">
                  {vertical ? (
                    <div className="mx-auto w-full max-w-[340px]" style={{ aspectRatio: "9 / 16" }}>
                      <iframe src={src} title={v[`title_${lang}`] || v.title_zh} className="h-full w-full" allowFullScreen frameBorder="0" scrolling="no" allow="encrypted-media;" />
                    </div>
                  ) : (
                    <AspectRatio ratio={16 / 9}>
                      <iframe src={src} title={v[`title_${lang}`] || v.title_zh} className="h-full w-full" allowFullScreen frameBorder="0" />
                    </AspectRatio>
                  )}
                  <div className="p-3 font-semibold text-sm flex items-center gap-2">
                    <PlayCircle className="h-4 w-4 text-[hsl(var(--brand-bronze))]" />
                    {v[`title_${lang}`] || v.title_zh}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
